import {Component, OnInit} from '@angular/core';
import {AuthRestService} from 'app/core/modules/rest/authentication/auth-rest.service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {AppState} from '@core/reducers';
import {CoreReducer} from '@core/store';
import {UserManagementActions, UserManagementReducer} from '../../store';
import {Observable} from 'rxjs';
import {LoggedUserDataModel} from '@core/modules/rest/user/response/logged-user-data.model';
import {UserDataModel} from '@core/modules/rest/user/response/user-data.model';
import {CreateUserRequestModel} from '@core/modules/rest/user/request/create-user-request.model';
import * as fromActions from '../../store/user-management-actions';
import {KeycloakService} from 'keycloak-angular';
import {WELCOME} from '@core/services/routes.service';
import {Router} from '@angular/router';
import {CreateUserPopupComponent} from '../popups/create-user-popup/create-user-popup.component';
import {CreateUserPopupModel} from '../popups/create-user-popup/model/create-user-popup.model';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter, take} from 'rxjs/operators';
import {IndicationIdentifierEnum} from '@core/modules/rest/indication/enums/indication-identifier.enum';
import {LanguageStyleEnum} from '@core/enums/language-style.enum';
import {IndicationRestService} from '@core/modules/rest/indication/indication-rest.service';
import {ConsentUseCaseRestService} from '@core/modules/rest/consent-use-case/consent-use-case-rest.service';
import {getLanguageEnumByLanguageCode, LanguageEnum} from '@core/enums/language.enum';
import {ConsentUseCaseBasicModel} from '@core/modules/rest/consent-use-case/response/consent-use-case-basic.model';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {NotificationService} from '@shared/components/notification/service/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {IndicationSimpleModel} from '@core/modules/rest/indication/response/indication-simple.model';
import {IndicationBasicWithChapterNumberModel} from '@core/modules/rest/indication/response/indication-basic-with-chapter-number.model';
import {ChooseEducationPopupComponent} from '../popups/choose-education-popup/choose-education-popup.component';
import {SaveEducationSettingsRequestModel} from '@core/modules/rest/user/request/save-education-settings-request.model';
import {PersonalSettingsModel} from '@shared/model/personal-settings.model';
import {KeycloakApplicationRolesEnum} from '@core/modules/rest/user/enums/keycloak-application-roles.enum';
import {SearchDataModel} from '@shared/model/search-data.model';

@UntilDestroy()
@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss']
})

export class UserListComponent implements OnInit {
    loggedUserInfo$: Observable<LoggedUserDataModel> = this.store.select(CoreReducer.loggedUserDataSelector);
    organizationUsers$: Observable<UserDataModel[]> = this.store.select(UserManagementReducer.organizationUsersDataSelector);
    dialogConfig = new MatDialogConfig();
    organizationUsers: UserDataModel[] = [];
    organization: string;
    organizationDefaultSettings: UserSettingsModel;
    indications: IndicationBasicWithChapterNumberModel[] = [];
    useCases: ConsentUseCaseBasicModel[] = [];
    offset = 0;
    searchText = '';
    hasEducation: boolean;
    saveDisabled: boolean;

    private CUSTOM_USE_CASE = 'CUSTOM_USE_CASE';

    constructor(private store: Store<AppState>, private authService: AuthRestService, private dialog: MatDialog,
                private keycloakService: KeycloakService, private router: Router, private userService: UserRestService,
                private indicationRestService: IndicationRestService, private consentUseCaseRestService: ConsentUseCaseRestService,
                private notificationService: NotificationService, private translate: TranslateService) {
    }

    ngOnInit(): void {
        this.store.dispatch(fromActions.ResetState());
        this.loggedUserInfo$.pipe(untilDestroyed(this))
            .subscribe((loggedUserData: LoggedUserDataModel) => {
                if (loggedUserData && loggedUserData.groups) {
                    this.organization = loggedUserData.groups[0];
                    this.getOrganizationUsers();
                }
            });

        this.organizationUsers$
            .subscribe((data: UserDataModel[]) => {
                this.organizationUsers = data.slice().sort((a, b) => this.getTime(new Date(b.createdAt)) - this.getTime(new Date(a.createdAt)));
            });

        this.userService.getOrganizationDefaultSettings().pipe(filter(value => !!value), take(1)).subscribe(data => {
            this.organizationDefaultSettings = data;
        });

        const language = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        this.indicationRestService.getOrganizationIndicationsWithAUnitsNumber(language)
            .pipe(filter(value => !!value), take(1)).subscribe(indications => {
            this.indications = indications;
            const amd = indications.find(indication => indication.indicationIdentifier === IndicationIdentifierEnum.AMD);

            // USE CASE FIX =======================================
            let amdId;
            if (amd === null || amd === undefined) {
                this.indicationRestService.getIndicationsSimple(language).pipe(filter(value => !!value), take(1)).subscribe((indcs: IndicationSimpleModel[]) => {
                    amdId = indcs.find(indication => indication.identifier === IndicationIdentifierEnum.AMD).id;
                    this.getUseCasesForOriginalIndication(amdId, language);
                });
            } else {
                amdId = amd.id;
                this.getUseCasesForOriginalIndication(amdId, language);
            }
            // ====================================================
        });
    }

    getUseCasesForOriginalIndication(amdId: string, language: LanguageEnum): void {
        this.consentUseCaseRestService.getConsentUseCases(amdId, language, LanguageStyleEnum.EASY)
            .pipe(filter(value => !!value), take(1)).subscribe(chapters => {
            this.useCases = chapters;
            this.useCases.push({
                id: this.CUSTOM_USE_CASE,
                title: this.translate.instant('GENERAL.SELECTION_BY_PATIENT'),
                uniqueTitle: this.translate.instant('GENERAL.SELECTION_BY_PATIENT'),
                description: '',
                durationInMilliseconds: null,
                chapterUnits: null,
                provisionDuration: null
            });
        });
    }

    educationExists($event: boolean): void {
        this.hasEducation = $event;
    }

    getTime(date ?: Date): number {
        return date != null ? date.getTime() : 0;
    }

    onLogOut(): void {
        this.authService.logout();
    }

    createUserDialog(): void {
        const dialogConfig = this.getDefaultDialogConfig();
        const dialogRef = this.dialog.open(CreateUserPopupComponent, dialogConfig);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
        dialogRef.componentInstance.resetEmailExistValidationEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.componentInstance.emailExists = false;
        });
        dialogRef.componentInstance.createUserEvent.pipe(untilDestroyed(this)).subscribe((request: CreateUserPopupModel) => {

            const userDataObject: CreateUserRequestModel = {
                name: request.name,
                email: request.email,
                organization: this.organization,
                roles: request.roles,
                basicMode: request.basicMode
            };
            this.store.dispatch(fromActions.CreateUser({
                request: userDataObject,
                executed: (done: boolean, statusCode: number, savedUser: UserDataModel, emailInUse: boolean) => {
                    if (done) {
                        dialogRef.close();
                        if (savedUser.roles.indexOf(KeycloakApplicationRolesEnum.PATIENT) > -1) {
                            this.notificationService.success(this.translate.instant('NOTIFICATION.USER_CREATED'));
                            this.openEducationDialog(savedUser);
                        } else {
                            this.notificationService.success(this.translate.instant('NOTIFICATION.USER_CREATED'));
                        }
                    } else {
                        if (statusCode === 406) {
                            dialogRef.componentInstance.showSpecialCharacterWarning = true;
                        } else {
                            dialogRef.componentInstance.emailExists = emailInUse;
                        }
                    }
                }
            }));
        });
    }

    openEducationDialog(user: UserDataModel): void {
        const personalSettings: PersonalSettingsModel = JSON.parse(user.personalSettings);
        const userSettings: UserSettingsModel = JSON.parse(user.settings);
        const dialogConfig = this.getDefaultDialogConfig();
        let availableIndications: IndicationBasicWithChapterNumberModel[];
        if (userSettings.basicMode) {
            availableIndications = this.indications;
        } else {
            availableIndications = this.indications.filter(item => !item.basicIndication);
        }

        let selectedIndicationId;
        if (personalSettings && personalSettings.indicationId) {
            selectedIndicationId = personalSettings.indicationId;
            this.hasEducation = true;
        } else {
            this.hasEducation = false;
        }
        dialogConfig.data = {
            userId: user.userId,
            indications: availableIndications,
            useCases: this.useCases,
            selectedIndicationId,
            selectedUseCase: personalSettings?.useCaseId,
            allowCustomUseCase: !userSettings?.customUseCaseOff,
            hasEducation: this.hasEducation,
            saveDisabled: this.saveDisabled
        };

        const dialogRef = this.dialog.open(ChooseEducationPopupComponent, dialogConfig);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
        dialogRef.componentInstance.saveEducationDataEvent.pipe(untilDestroyed(this)).subscribe((data: SaveEducationSettingsRequestModel) => {
            this.store.dispatch(fromActions.SaveUserEducationSettings({
                request: data,
                executed: () => {
                    dialogRef.close();
                    if (this.hasEducation === false) {
                        this.notificationService.success(this.translate.instant('NOTIFICATION.EMAIL_INVITATION_SENT'));
                    }
                }
            }));
        });
    }

    getDefaultDialogConfig(): MatDialogConfig {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = true;
        return dialogConfig;
    }

    onBack(): void {
        this.router.navigate([WELCOME]);
    }

    filterUsers(text: string): void {
        this.searchText = text;
        this.offset = 0;
        this.store.dispatch(fromActions.ResetState());
        if (this.searchText.length > 0) {
            this.getFilteredOrganizationUsers();
        } else {
            this.getOrganizationUsers();
        }
    }

    onTableScroll(): void {
        if (this.searchText.length === 0) {
            this.offset++;
            this.getOrganizationUsers();
        }
    }

    private getOrganizationUsers(): void {
        this.store.dispatch(UserManagementActions.GetUsersByOrganization({
            organization: this.organization,
            offset: this.offset
        }));
    }

    private getFilteredOrganizationUsers(): void {
        const searchData: SearchDataModel = {
            filter: this.searchText
        };
        this.store.dispatch(UserManagementActions.GetFilteredUsersByOrganization({
            organization: this.organization,
            searchData
        }));
    }
}
